import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  Boxes, Plus, Edit2, Trash2, ArrowDown, ArrowUp, ArrowLeftRight,
  RefreshCw, AlertTriangle, History,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MOVEMENT_META = {
  receive: { label: 'Receive', icon: ArrowDown, color: 'text-green-600 bg-green-100' },
  issue: { label: 'Issue', icon: ArrowUp, color: 'text-red-600 bg-red-100' },
  transfer: { label: 'Transfer', icon: ArrowLeftRight, color: 'text-blue-600 bg-blue-100' },
  return: { label: 'Return', icon: RefreshCw, color: 'text-amber-600 bg-amber-100' },
  damaged: { label: 'Damaged', icon: AlertTriangle, color: 'text-red-600 bg-red-100' },
  adjust: { label: 'Adjust', icon: RefreshCw, color: 'text-purple-600 bg-purple-100' },
};

const emptyItemForm = {
  name: '', category: '', quantity: 0, unit: 'pcs',
  minimum_stock: 0, reorder_threshold: 0, storage_location: '',
  unit_cost: 0, supplier: '', notes: '',
};

const emptyMoveForm = {
  item_id: '', movement_type: 'receive', quantity: '',
  date: format(new Date(), 'yyyy-MM-dd'), performed_by: '',
  location: '', destination_location: '', reason: '', notes: '',
};

const InventoryPage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'finance_officer', 'project_manager', 'school_head']);
  const canMove = hasRole(['admin', 'finance_officer', 'project_manager', 'school_head', 'staff']);

  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemDialog, setItemDialog] = useState(false);
  const [moveDialog, setMoveDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [itemForm, setItemForm] = useState({ ...emptyItemForm });
  const [moveForm, setMoveForm] = useState({ ...emptyMoveForm });
  const [showLowOnly, setShowLowOnly] = useState(false);

  const fetchMeta = useCallback(async () => {
    try { const r = await axios.get(`${API_URL}/api/assets/meta`); setMeta(r.data); } catch (e) { /* noop */ }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [iRes, mRes] = await Promise.all([
        axios.get(`${API_URL}/api/inventory${showLowOnly ? '?low_stock_only=true' : ''}`),
        axios.get(`${API_URL}/api/inventory/movements`),
      ]);
      setItems(iRes.data);
      setMovements(mRes.data);
    } catch (e) {
      toast.error('Failed to load inventory');
    } finally { setLoading(false); }
  }, [showLowOnly]);

  useEffect(() => { fetchMeta(); }, [fetchMeta]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreateItem = () => { setEditing(null); setItemForm({ ...emptyItemForm }); setItemDialog(true); };
  const openEditItem = (i) => { setEditing(i); setItemForm({ ...emptyItemForm, ...i }); setItemDialog(true); };

  const submitItem = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...itemForm,
        quantity: parseFloat(itemForm.quantity || 0),
        minimum_stock: parseFloat(itemForm.minimum_stock || 0),
        reorder_threshold: parseFloat(itemForm.reorder_threshold || 0),
        unit_cost: parseFloat(itemForm.unit_cost || 0),
      };
      if (editing) {
        await axios.put(`${API_URL}/api/inventory/${editing.id}`, payload);
        toast.success('Item updated');
      } else {
        await axios.post(`${API_URL}/api/inventory`, payload);
        toast.success('Item added');
      }
      setItemDialog(false); setEditing(null); setItemForm({ ...emptyItemForm });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const deleteItem = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/inventory/${id}`);
      toast.success('Item deleted');
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openMove = (item, type = 'receive') => {
    setMoveForm({ ...emptyMoveForm, item_id: item.id, movement_type: type });
    setMoveDialog(true);
  };

  const submitMove = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/inventory/movements`, {
        ...moveForm,
        quantity: parseFloat(moveForm.quantity),
      });
      toast.success('Stock movement recorded');
      setMoveDialog(false);
      setMoveForm({ ...emptyMoveForm });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to record'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-heading text-2xl font-semibold">Inventory Management</h2>
          <p className="text-muted-foreground">Track consumables, stock levels and movements</p>
        </div>
        <div className="flex gap-2">
          <ExportButton path="exports/list/inventory" filenameBase="suubi_inventory" testId="inventory-export-btn" />
          <Button variant={showLowOnly ? 'default' : 'outline'} size="sm" onClick={() => setShowLowOnly(!showLowOnly)}>
            <AlertTriangle className="h-4 w-4 mr-1" /> {showLowOnly ? 'All Items' : 'Low Stock Only'}
          </Button>
          {canEdit && (
            <Button onClick={openCreateItem} data-testid="add-inventory-btn">
              <Plus className="mr-1 h-4 w-4" /> Add Item
            </Button>
          )}
        </div>
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items" data-testid="tab-inventory-items">Items ({items.length})</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-inventory-movements">Movement History ({movements.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="items" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Reorder Threshold</TableHead>
                    <TableHead>Storage Location</TableHead>
                    <TableHead className="text-right">Unit Cost</TableHead>
                    <TableHead>Supplier</TableHead>
                    {canMove && <TableHead className="w-72"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-10">Loading...</TableCell></TableRow>
                  ) : items.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-10 text-muted-foreground">No items</TableCell></TableRow>
                  ) : (
                    items.map((i) => {
                      const isLow = (i.quantity ?? 0) <= (i.reorder_threshold ?? 0);
                      return (
                        <TableRow key={i.id} className="table-row-hover" data-testid={`inventory-row-${i.id}`}>
                          <TableCell className="font-medium">{i.name}</TableCell>
                          <TableCell><Badge variant="outline">{i.category}</Badge></TableCell>
                          <TableCell>
                            <span className={isLow ? 'text-red-600 font-bold' : 'font-medium'}>
                              {i.quantity} {i.unit || 'pcs'}
                            </span>
                            {isLow && <Badge className="ml-2 bg-red-100 text-red-700">Low</Badge>}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{i.reorder_threshold ?? 0}</TableCell>
                          <TableCell className="capitalize text-sm">{i.storage_location?.replace(/_/g, ' ') || '—'}</TableCell>
                          <TableCell className="text-right">{i.unit_cost ? formatCurrency(i.unit_cost) : '—'}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{i.supplier || '—'}</TableCell>
                          {canMove && (
                            <TableCell>
                              <div className="flex gap-1 justify-end flex-wrap">
                                <Button size="sm" variant="outline" className="text-green-700 border-green-200" onClick={() => openMove(i, 'receive')} data-testid={`receive-${i.id}`}><ArrowDown className="h-3 w-3 mr-1" /> Receive</Button>
                                <Button size="sm" variant="outline" className="text-red-700 border-red-200" onClick={() => openMove(i, 'issue')} data-testid={`issue-${i.id}`}><ArrowUp className="h-3 w-3 mr-1" /> Issue</Button>
                                {canEdit && (
                                  <>
                                    <Button size="sm" variant="outline" onClick={() => openEditItem(i)} data-testid={`edit-inventory-${i.id}`} className="h-8 px-2.5">
                                      <Edit2 className="h-3.5 w-3.5 mr-1" />Edit
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => setDeleteTarget(i)} data-testid={`delete-inventory-${i.id}`} className="h-8 px-2.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive">
                                      <Trash2 className="h-3.5 w-3.5 mr-1" />Delete
                                    </Button>
                                  </>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="movements" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>By</TableHead>
                    <TableHead>Location</TableHead>
                    <TableHead>After</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movements.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No movements recorded</TableCell></TableRow>
                  ) : (
                    movements.map((m) => {
                      const item = items.find(it => it.id === m.item_id);
                      const meta = MOVEMENT_META[m.movement_type] || {};
                      const Icon = meta.icon;
                      return (
                        <TableRow key={m.id}>
                          <TableCell>{m.date}</TableCell>
                          <TableCell>
                            <Badge className={meta.color || ''}>
                              {Icon && <Icon className="h-3 w-3 mr-1 inline" />}
                              {meta.label || m.movement_type}
                            </Badge>
                          </TableCell>
                          <TableCell>{item?.name || m.item_id.slice(0, 8)}</TableCell>
                          <TableCell className="font-medium">{m.quantity}</TableCell>
                          <TableCell className="text-sm">{m.performed_by || '—'}</TableCell>
                          <TableCell className="text-sm capitalize">
                            {m.location?.replace(/_/g, ' ') || '—'}
                            {m.destination_location && ` → ${m.destination_location.replace(/_/g, ' ')}`}
                          </TableCell>
                          <TableCell className="font-medium">{m.after_quantity ?? '—'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Item Dialog */}
      <Dialog open={itemDialog} onOpenChange={(o) => { setItemDialog(o); if (!o) { setEditing(null); setItemForm({ ...emptyItemForm }); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Edit' : 'Add'} Inventory Item</DialogTitle>
            <DialogDescription>Track a consumable or stock item</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitItem} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Name *</Label><Input value={itemForm.name} onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })} required data-testid="inventory-name-input" /></div>
              <div className="space-y-1.5"><Label>Category *</Label><Input value={itemForm.category} onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })} placeholder="e.g., Stationery" required /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" step="0.01" value={itemForm.quantity} onChange={(e) => setItemForm({ ...itemForm, quantity: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Unit</Label><Input value={itemForm.unit} onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })} placeholder="pcs, kg..." /></div>
              <div className="space-y-1.5"><Label>Unit Cost</Label><Input type="number" value={itemForm.unit_cost} onChange={(e) => setItemForm({ ...itemForm, unit_cost: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Minimum Stock</Label><Input type="number" value={itemForm.minimum_stock} onChange={(e) => setItemForm({ ...itemForm, minimum_stock: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Reorder Threshold</Label><Input type="number" value={itemForm.reorder_threshold} onChange={(e) => setItemForm({ ...itemForm, reorder_threshold: e.target.value })} /></div>
            </div>
            <div className="space-y-1.5">
              <Label>Storage Location</Label>
              <Select value={itemForm.storage_location} onValueChange={(v) => setItemForm({ ...itemForm, storage_location: v })}>
                <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{meta?.locations?.map(l => <SelectItem key={l} value={l}>{l.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Supplier</Label><Input value={itemForm.supplier} onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Notes</Label><Textarea rows={2} value={itemForm.notes} onChange={(e) => setItemForm({ ...itemForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setItemDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="inventory-submit-btn">{editing ? 'Update' : 'Add'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Stock Movement Dialog */}
      <Dialog open={moveDialog} onOpenChange={setMoveDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Record Stock Movement</DialogTitle>
            <DialogDescription>Update stock levels and create an audit trail</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitMove} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Movement Type *</Label>
                <Select value={moveForm.movement_type} onValueChange={(v) => setMoveForm({ ...moveForm, movement_type: v })}>
                  <SelectTrigger data-testid="movement-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(MOVEMENT_META).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Quantity *</Label><Input type="number" step="0.01" value={moveForm.quantity} onChange={(e) => setMoveForm({ ...moveForm, quantity: e.target.value })} required data-testid="movement-quantity-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={moveForm.date} onChange={(e) => setMoveForm({ ...moveForm, date: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Performed By</Label><Input value={moveForm.performed_by} onChange={(e) => setMoveForm({ ...moveForm, performed_by: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Location</Label>
                <Select value={moveForm.location} onValueChange={(v) => setMoveForm({ ...moveForm, location: v })}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{meta?.locations?.map(l => <SelectItem key={l} value={l}>{l.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {moveForm.movement_type === 'transfer' && (
                <div className="space-y-1.5">
                  <Label>Destination Location</Label>
                  <Select value={moveForm.destination_location} onValueChange={(v) => setMoveForm({ ...moveForm, destination_location: v })}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{meta?.locations?.map(l => <SelectItem key={l} value={l}>{l.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-1.5"><Label>Reason / Notes</Label><Textarea rows={2} value={moveForm.notes} onChange={(e) => setMoveForm({ ...moveForm, notes: e.target.value })} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setMoveDialog(false)}>Cancel</Button>
              <Button type="submit" data-testid="movement-submit-btn">Record</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-inventory-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete inventory item?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget ? (
                <>Delete <strong>{deleteTarget.name}</strong> ({deleteTarget.sku || deleteTarget.category})? This action cannot be undone.</>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteItem(deleteTarget.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-inventory-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InventoryPage;
